import { useState, useMemo } from "react";
import { num, toM, m2, money, UNITS, CalcLayout, FormCol, Card, Field, Sel, HowTo, Advanced, AreaTable, sumArea, ResultCard, useCatalog, ProductSelect } from "./shared";

export default function PanelesAcusticos({ onSave, s }) {
  const items = useCatalog("paneles_acusticos");
  const [pid, setPid] = useState("");
  const prod = items.find((p) => p.id === pid);
  const [u, setU] = useState("m");
  const [tipo, setTipo] = useState("PET");
  const [superficies, setSuperficies] = useState([{ nombre: "Muro 1", largo: "5", ancho: "2.80", cant: "1" }]);
  const [descuentos, setDescuentos] = useState([]);
  const [cobertura, setCobertura] = useState("40");
  const [panelLargo, setPanelLargo] = useState("1.20");
  const [panelAncho, setPanelAncho] = useState("0.60");
  const [piezasCaja, setPiezasCaja] = useState("0");
  const [reserva, setReserva] = useState("1");
  const [desp, setDesp] = useState("8");
  const [fijacion, setFijacion] = useState("Adhesivo");
  const [adhRend, setAdhRend] = useState("3");
  const [precio, setPrecio] = useState("");
  const [precioInst, setPrecioInst] = useState("");
  const pr = prod ? prod.price : num(precio);

  const r = useMemo(() => {
    const areaTotal = sumArea(superficies, u), areaDesc = sumArea(descuentos, u);
    const areaNeta = Math.max(0, areaTotal - areaDesc);
    const areaObjetivo = areaNeta * (num(cobertura) / 100);
    const areaPanel = toM(panelLargo, u) * toM(panelAncho, u);
    const teoricos = areaPanel > 0 ? Math.ceil(areaObjetivo / areaPanel) : 0;
    const ajustados = Math.ceil(teoricos * (1 + num(desp) / 100));
    const aVender = ajustados + Math.max(0, Math.round(num(reserva)) || 0);
    const cajas = num(piezasCaja) > 0 ? Math.ceil(aVender / num(piezasCaja)) : 0;
    const coberturaReal = aVender * areaPanel;
    const pctReal = areaNeta > 0 ? (coberturaReal / areaNeta) * 100 : 0;
    const adhesivo = fijacion === "Adhesivo" && num(adhRend) > 0 ? Math.ceil(coberturaReal / num(adhRend)) : 0;
    const tornillos = fijacion !== "Adhesivo" ? aVender * 4 : 0;
    const cMat = aVender * pr;
    const total = cMat + num(precioInst);
    const w = [];
    if (areaPanel <= 0) w.push("Captura la medida del panel.");
    if (areaNeta === 0) w.push("Captura al menos una superficie con medidas.");
    if (prod && prod.stock < aVender) w.push(`Inventario insuficiente de "${prod.name}": stock ${prod.stock}, requieres ${aVender} paneles.`);
    const rows = [
      { section: "Proyecto" },
      { label: "Tipo de panel", value: tipo }, { label: "Superficies", value: `${superficies.length}` },
      { label: "Área total", value: m2(areaTotal) }, { label: "Área descontada", value: m2(areaDesc) },
      { label: "Área neta disponible", value: m2(areaNeta) }, { label: "Cobertura deseada", value: `${num(cobertura)}%` },
      { label: "Área objetivo a tratar", value: m2(areaObjetivo) },
      { section: "Paneles" },
      { label: "Área por panel", value: m2(areaPanel) }, { label: "Paneles teóricos", value: `${teoricos}` },
      { label: "Desperdicio aplicado", value: `${num(desp)}%` }, { label: "Paneles ajustados", value: `${ajustados}` },
      { label: "Reserva", value: `${Math.max(0, Math.round(num(reserva)) || 0)}` },
      { label: "Paneles a vender", value: `${aVender} pza(s)`, strong: true },
      ...(cajas ? [{ label: "Cajas / paquetes", value: `${cajas}` }] : []),
      { label: "Cobertura real", value: m2(coberturaReal) }, { label: "% real de cobertura", value: `${pctReal.toFixed(1)}%` },
      { section: "Fijación / complementos" },
      { label: "Sistema de fijación", value: fijacion },
      ...(adhesivo ? [{ label: "Adhesivo (unidades)", value: `${adhesivo}` }] : []),
      ...(tornillos ? [{ label: "Tornillos / clips (aprox.)", value: `${tornillos}` }] : []),
      { section: "Costos" },
      { label: "Materiales", value: money(cMat) }, { label: "Instalación", value: money(num(precioInst)) },
      { label: "Total estimado", value: money(total), strong: true },
    ];
    return { rows, warnings: w, total, inputs: { u, tipo } };
  }, [u, tipo, superficies, descuentos, cobertura, panelLargo, panelAncho, piezasCaja, reserva, desp, fijacion, adhRend, pr, precioInst, prod]);

  return (
    <CalcLayout>
      <FormCol>
        <HowTo title="¿Cómo calcular paneles acústicos?"><p>Define las superficies y el porcentaje de cobertura deseado. El sistema calcula los paneles a partir del área objetivo, agrega desperdicio, reserva, cajas y la fijación (adhesivo o tornillos/clips).</p></HowTo>
        <Card>
          <Sel label="Unidad" value={u} onChange={setU} opts={UNITS} testid="pa-unidad" />
          <Sel label="Tipo de panel" value={tipo} onChange={setTipo} opts={["PET", "Fibra de poliéster", "Madera ranurada", "Espuma perfilada", "Modular", "Baffle / Nube"]} testid="pa-tipo" />
          <Field label="Cobertura deseada (%)" value={cobertura} onChange={setCobertura} step="5" suffix="%" testid="pa-cobertura" />
          <Sel label="Sistema de fijación" value={fijacion} onChange={setFijacion} opts={["Adhesivo", "Tornillos", "Clips", "Suspensión"]} testid="pa-fijacion" />
        </Card>
        <ProductSelect items={items} value={pid} onChange={setPid} testid="pa-product" />
        <AreaTable title="Superficies (ancho × alto)" rows={superficies} setRows={setSuperficies} u={u} defaultName="Muro" testid="pa-sup" />
        <AreaTable title="Descontar (puertas/luminarias)" rows={descuentos} setRows={setDescuentos} u={u} addLabel="+ Descuento" defaultName="Hueco" testid="pa-desc" />
        <Card>
          <Field label="Largo del panel" value={panelLargo} onChange={setPanelLargo} suffix={u} testid="pa-panellargo" />
          <Field label="Ancho del panel" value={panelAncho} onChange={setPanelAncho} suffix={u} testid="pa-panelancho" />
          <Field label="Piezas por caja (0 = por pieza)" value={piezasCaja} onChange={setPiezasCaja} step="1" testid="pa-piezascaja" />
          <Field label="Paneles de reserva" value={reserva} onChange={setReserva} step="1" testid="pa-reserva" />
        </Card>
        <Advanced>
          <Field label="Desperdicio (%)" value={desp} onChange={setDesp} step="1" suffix="%" testid="pa-desp" />
          <Field label="Rendimiento adhesivo (m²/unidad)" value={adhRend} onChange={setAdhRend} step="0.5" suffix="m²" testid="pa-adhrend" />
          <Field label="Precio por panel ($)" value={prod ? prod.price : precio} onChange={setPrecio} suffix="$" testid="pa-precio" />
          <Field label="Instalación ($)" value={precioInst} onChange={setPrecioInst} suffix="$" testid="pa-precio-inst" />
        </Advanced>
      </FormCol>
      <ResultCard title="Cotización de Paneles Acústicos — GOÛT" modality="paneles_acusticos" rows={r.rows} warnings={r.warnings} total={r.total} inputs={r.inputs} onSave={onSave} />
    </CalcLayout>
  );
}
